import { useState, useCallback, useEffect } from 'react';
import { getRecommendations } from '../components/API';
import { useFilters } from '../context/FilterProvider';
import { calculateDose } from '../util/RecUtil';
import { latestTestDate } from '../util/GenericUtil';
import {
    recommendationsToEntries,
    groupSupplementsByCategory,
    applyCategoryCutoff,
    mergeRegeneratedEntries,
    normalizeProtocolEntries,
} from '../util/ProtocolUtil';
import bloodGroupings from '../../lambda/health-recommendations/blood_groupings.json';

export function useRecommendations({ filteredTests, getFxRange, sex, savedProtocol, protocolLocked, effectiveLocked, saveProtocol, doctorViewMode }) {
    const { selectedSystem, setHighlightedRecommendations } = useFilters();
    const [recommendations, setRecommendations] = useState(null);
    const [recommendationMode, setRecommendationMode] = useState('support');
    const [loadingRecommendations, setLoadingRecommendations] = useState(false);
    const [selectedAlternatives, setSelectedAlternatives] = useState({});

    // Initialize from saved protocol on mount
    useEffect(() => {
        if (savedProtocol?.rawRecommendations && !recommendations) {
            setRecommendations(savedProtocol.rawRecommendations);
        }
    }, [savedProtocol]);

    // Sync highlighted recommendations when selectedSystem or recommendations change
    useEffect(() => {
        if (!selectedSystem || !recommendations?.supplements || !bloodGroupings[selectedSystem]) {
            setHighlightedRecommendations([]);
            return;
        }
        const systemMarkers = Object.keys(bloodGroupings[selectedSystem].markers);
        const relatedRecs = recommendations.supplements
            .filter(supp => supp.contributing_markers?.some(m => systemMarkers.includes(typeof m === 'string' ? m : m?.marker)))
            .map(r => r.category);
        setHighlightedRecommendations(relatedRecs);
    }, [selectedSystem, recommendations]);

    const handleSelectAlternative = useCallback((category, productName) => {
        setSelectedAlternatives(prev => ({
            ...prev,
            [category]: productName
        }));
    }, []);

    const handleGetAllDefaults = useCallback(() => {
        const supplements = recommendations?.supplements || [];
        const defaults = supplements.map(supp => {
            const doseCalc = calculateDose(supp.product_details, supp.total_weight, recommendationMode);
            return {
                product: supp.supplement,
                category: supp.category,
                dose: doseCalc?.dailyTotal,
                unit: supp.product_details?.unit,
                frequency: doseCalc?.frequency,
                timing: supp.product_details?.empty_stomach ? 'empty stomach' : 'with food',
                special_instructions: supp.product_details?.special_instructions,
                link: supp.product_details?.metadata?.link
            };
        });
        return defaults;
    }, [recommendations, recommendationMode]);

    const handleGetRecommendations = useCallback(async (mode = 'replace') => {
        if (!filteredTests || filteredTests.length === 0) {
            alert("No lab tests available for recommendations");
            return;
        }

        setLoadingRecommendations(true);
        try {
            // The date of the panel being scored, NOT today. The lambda parses
            // this with strptime('%Y-%m-%d') and echoes it back as
            // metadata.testdate_used, which is persisted on the protocol and
            // drives era/staleness/trend logic downstream. Hardcoding today
            // silently mislabelled every run.
            const testdate = latestTestDate(filteredTests);
            // The lambda skips any entry whose testdate it cannot parse and then
            // 400s, so the payload falls back to today to keep scoring working
            // when a file somehow carries no header date. testdateUsed below is
            // NOT given that fallback — an unknown date is persisted as null
            // rather than fabricated.
            const lab_data_list = [{
                header: {
                    testdate: testdate || new Date().toISOString().split('T')[0],
                    patient_id: "web-user"
                },
                body: filteredTests.map(test => ({
                    test_name: test.test_name || test.alias,
                    result: parseFloat(test.result),
                    lab_low: parseFloat(test.lab_low),
                    lab_high: parseFloat(test.lab_high),
                    units: test.units
                }))
            }];

            const fx_data = { body: {} };
            filteredTests.forEach(test => {
                const testName = test.test_name || test.alias;
                const fxRange = getFxRange(testName, sex);
                if (fxRange) {
                    fx_data.body[testName] = {
                        fx_low: parseFloat(fxRange.fx_low),
                        fx_high: parseFloat(fxRange.fx_high),
                        units: fxRange.units
                    };
                }
            });

            const response = await getRecommendations(lab_data_list, fx_data);

            if (response && response.supplements) {
                setRecommendations(response);

                // Auto-save for unlocked patients.
                // Only categories that pass the display cutoff become entries —
                // protocol.json entries always match what the user sees. The full
                // candidate pool stays available via rawRecommendations.
                //
                // The Replace/Add mode governs ONLY the generated set; manual
                // contributions (source !== 'generated') and their dose edits/order
                // are always preserved via mergeRegeneratedEntries.
                // A doctor may regenerate a locked protocol (PROTOCOL_PERMISSIONS.md);
                // only a patient is blocked by the lock, and by the EFFECTIVE lock
                // so a stale flag left by a removed doctor does not block them.
                // Falls back to the raw flag when effectiveLocked is not supplied.
                const lockBlocksSave = effectiveLocked != null ? effectiveLocked : protocolLocked;
                if (saveProtocol && (doctorViewMode || !lockBlocksSave)) {
                    // Stamp the generator's acting role as the entry author so a
                    // doctor's regenerated entries lock for the patient. Author is
                    // orthogonal to source (provenance stays source === 'generated').
                    const authorRole = doctorViewMode ? 'doctor' : 'patient';

                    const visibleGroups = applyCategoryCutoff(
                        groupSupplementsByCategory(response.supplements)
                    );
                    const visibleSupplements = visibleGroups.flatMap(g => g.products);
                    const freshGenerated = recommendationsToEntries(visibleSupplements, recommendationMode, authorRole);

                    // Normalize saved entries to the canonical { source, author } shape
                    // so the generated/manual split is consistent across legacy data.
                    const normalized = normalizeProtocolEntries(savedProtocol);
                    const savedEntries = normalized?.entries || [];

                    let generated;
                    if (mode === 'add') {
                        // Union prior generated with fresh generated, deduped by name
                        // (fresh wins). Manual entries are preserved by the merge below.
                        const priorGenerated = savedEntries.filter(e => e.source === 'generated');
                        const freshNames = new Set(freshGenerated.map(e => e.name));
                        const retained = priorGenerated.filter(e => !freshNames.has(e.name));
                        generated = [...freshGenerated, ...retained];
                    } else {
                        generated = freshGenerated;
                    }

                    // mergeRegeneratedEntries preserves source !== 'generated' (manual
                    // contributions + their dose edits) and overlays the generated set.
                    const entries = mergeRegeneratedEntries(savedEntries, generated);
                    saveProtocol({
                        entries,
                        patientNotes: savedProtocol?.patientNotes || '',
                        doctorNotes: savedProtocol?.doctorNotes || '',
                        rawRecommendations: response,
                        testdateUsed: testdate,
                        // New protocol generation starts a new era: fresh generatedAt
                        // → new protocol_<era>.json file, compliance re-baselines.
                        generatedAt: new Date().toISOString(),
                    }).catch(err => console.error('Auto-save protocol failed:', err));
                }
            } else {
                console.warn("Unexpected response format:", response);
                setRecommendations(null);
            }
        } catch (error) {
            console.error("getRecommendations error:", error);
            setRecommendations(null);
        } finally {
            setLoadingRecommendations(false);
        }
    }, [filteredTests, getFxRange, sex, saveProtocol, savedProtocol, protocolLocked, recommendationMode, doctorViewMode]);

    return {
        recommendations,
        setRecommendations,
        recommendationMode,
        setRecommendationMode,
        loadingRecommendations,
        selectedAlternatives,
        handleSelectAlternative,
        handleGetRecommendations,
        handleGetAllDefaults,
    };
}
